import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { ChangePasswordDialogComponent } from './core/auth/change-password-dialog.component';
import { UserContextService } from './core/auth/user-context.service';
import { ENTITY_CONFIGS } from './core/config/entity-config';
import { InMemoryDataService } from './core/data/in-memory-data.service';
import { LanguageCode } from './core/i18n/i18n.types';
import { LocalizationService } from './core/i18n/localization.service';
import { TranslatePipe } from './core/i18n/translate.pipe';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    TranslatePipe
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly userContext = inject(UserContextService);
  private readonly auth = inject(AuthService);
  private readonly dataService = inject(InMemoryDataService);
  private readonly localization = inject(LocalizationService);
  private readonly mobileState = toSignal(this.breakpointObserver.observe('(max-width: 900px)'), {
    initialValue: { matches: false, breakpoints: {} }
  });
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly menuItems = ENTITY_CONFIGS.filter((item) => item.showInMenu !== false);
  readonly reportsEnabled = computed(() => this.auth.currentUser()?.productFeatures?.reportsEnabled === true);
  readonly notificationsEnabled = computed(() => {
    const role = this.currentUser().role;
    if (role !== 'admin' && role !== 'office') return false;
    const notifications = this.dataService.organisationSettings()?.notifications;
    return notifications
      ? Object.values(notifications).some((value) =>
          typeof value === 'object' && value !== null && (value.email === true || value.sms === true)
        )
      : false;
  });
  readonly instructorCalendarEnabled = this.userContext.canUseInstructorCalendar;
  readonly oskMenuItems = computed(() => [
    { path: '/dashboard', icon: 'dashboard', labelKey: 'nav.dashboard' },
    ...(this.instructorCalendarEnabled() ? [{ path: '/calendar/occupancy', icon: 'calendar_view_week', labelKey: 'nav.occupancy' }] : []),
    ...(this.reportsEnabled() ? [{ path: '/reports', icon: 'analytics', labelKey: 'nav.reports' }] : []),
    ...(this.notificationsEnabled() ? [{ path: '/notifications', icon: 'notifications', labelKey: 'nav.notifications' }] : []),
    ...this.menuItems.map((item) => ({
      path: `/resources/${item.key}`,
      icon: item.icon,
      labelKey: `entity.${item.key}`
    }))
  ]);
  readonly rootMenuItems = [
    { path: '/admin', icon: 'dashboard', labelKey: 'nav.dashboard' },
    { path: '/admin/centers', icon: 'apartment', labelKey: 'entity.organisations' },
    { path: '/admin/products', icon: 'inventory_2', labelKey: 'common.products' }
  ];
  readonly mobileMenuOpen = signal(false);
  readonly isMobile = computed(() => this.mobileState().matches);
  readonly users = this.userContext.users;
  readonly currentUser = this.userContext.currentUser;
  readonly accountDisplayName = computed(() => this.auth.currentUser()?.displayName || this.auth.currentUser()?.username || this.currentUser().displayName || 'Profil');
  readonly currentLanguage = this.userContext.currentLanguage;
  readonly lessonsRetrying = this.dataService.lessonsRetrying;
  readonly lessonsRetryMessage = this.dataService.lessonsRetryMessage;
  readonly canAccessStudentApp = this.userContext.canAccessStudentApp;
  readonly canAccessInstructorApp = this.userContext.canAccessInstructorApp;
  readonly canAccessUserManagement = this.userContext.canAccessUserManagement;
  readonly canManageUsers = computed(() => {
    const role = this.currentUser().role;
    return this.canAccessUserManagement() && (role === 'admin' || role === 'office');
  });
  readonly languageOptions: Array<{ code: LanguageCode; label: string }> = [
    { code: 'pl', label: 'Polski' },
    { code: 'en', label: 'English' },
    { code: 'ua', label: 'Українська' }
  ];
  readonly currentOrganisation = computed(() => {
    if (this.isAdminConsoleUrl(this.currentUrl())) {
      return null;
    }
    const organisationId = this.userContext.currentOrganisationId();
    if (!organisationId) {
      return null;
    }

    return this.dataService.organisations().find((item) => this.sameId(item.id, organisationId)) ?? null;
  });
  readonly toolbarTitle = computed(() => {
    if (this.isAdminConsoleUrl(this.currentUrl())) {
      return 'Admin OS Portal';
    }
    return this.sessionOrganisationTitle() || this.currentOrganisation()?.shortName || this.currentOrganisation()?.name || 'OS Portal';
  });
  readonly toolbarFullTitle = computed(() => {
    if (this.isAdminConsoleUrl(this.currentUrl())) {
      return 'Admin OS Portal';
    }
    return this.sessionOrganisationFullTitle() || this.currentOrganisation()?.name || this.toolbarTitle();
  });
  readonly toolbarLogoUrl = computed(() => this.currentOrganisation()?.logoUrl ?? '');
  readonly toolbarLogoError = signal(false);
  readonly toolbarLogoFallback = computed(() =>
    this.toolbarTitle()
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase()
  );
  readonly isContextMode = computed(() => {
    const url = this.currentUrl();
    return url.startsWith('/student') || url.startsWith('/instructor') || url.startsWith('/context') || this.isPublicAuthUrl(url);
  });
  readonly isLoginMode = computed(() => this.isPublicAuthUrl(this.currentUrl()));
  readonly isRootMode = computed(() => this.isAdminConsoleUrl(this.currentUrl()));
  readonly isInstructorMode = computed(() => this.currentUrl().startsWith('/instructor'));
  readonly shouldShowToolbarLogo = computed(() => !!this.toolbarLogoUrl() && !this.toolbarLogoError());
  readonly isContextSelectionPage = computed(() => this.currentUrl().startsWith('/context'));
  readonly showLogoutAction = computed(() => !this.isContextSelectionPage() && !this.isLoginMode());
  readonly toolbarInstructorProfile = computed(() => {
    if (!this.currentUrl().startsWith('/instructor')) {
      return null;
    }
    const linkedId = this.userContext.currentInstructorId();
    if (!linkedId) {
      return null;
    }
    const instructor = this.dataService.instructors().find((item) => item.id === linkedId);
    if (!instructor) {
      return null;
    }
    const initials = `${instructor.name.charAt(0)}${instructor.surname.charAt(0)}`.toUpperCase();
    return {
      initials,
      fullName: `${instructor.name} ${instructor.surname}`.trim()
    };
  });
  readonly toolbarStudentProfile = computed(() => {
    if (!this.currentUrl().startsWith('/student')) {
      return null;
    }
    const linkedId = this.userContext.currentStudentId();
    const student = linkedId ? this.dataService.students().find((item) => item.id === linkedId) : this.dataService.students()[0];
    if (!student) {
      return null;
    }
    const initials = `${student.name.charAt(0)}${student.surname.charAt(0)}`.toUpperCase();
    return {
      initials,
      fullName: `${student.name} ${student.surname}`.trim()
    };
  });
  readonly toolbarContextProfile = computed(() => this.toolbarInstructorProfile() ?? this.toolbarStudentProfile());
  readonly rootSupportSessionType = computed<'osk' | 'student' | 'instructor' | null>(() => {
    if (!this.auth.isDelegatedOrganisationSession() || this.isLoginMode() || this.isRootMode()) {
      return null;
    }
    const url = this.currentUrl();
    if (url.startsWith('/student')) {
      return 'student';
    }
    if (url.startsWith('/instructor')) {
      return 'instructor';
    }
    return 'osk';
  });
  readonly rootSupportSessionLabel = computed(() => {
    const type = this.rootSupportSessionType();
    return type ? this.localization.translate(`toolbar.rootSupport.${type}`) : '';
  });


  private sameId(left: unknown, right: unknown): boolean {
    return `${left ?? ''}`.trim() === `${right ?? ''}`.trim();
  }

  private sessionOrganisationTitle(): string {
    const names = this.sessionOrganisationNames();
    return names.shortName || names.name;
  }

  private sessionOrganisationFullTitle(): string {
    return this.sessionOrganisationNames().name || this.sessionOrganisationTitle();
  }

  private sessionOrganisationNames(): { name: string; shortName: string } {
    const user = this.auth.currentUser() as
      | {
          organisationName?: string | null;
          organisationShortName?: string | null;
          organizationName?: string | null;
          organizationShortName?: string | null;
        }
      | null;
    const name = this.firstText(user?.organisationName, user?.organizationName);
    const shortName = this.firstText(user?.organisationShortName, user?.organizationShortName, name);
    return { name, shortName };
  }

  private firstText(...values: unknown[]): string {
    return values.map((value) => `${value ?? ''}`.trim()).find((value) => value.length > 0) ?? '';
  }

  private readonly resetLogoState = effect(
    () => {
      this.toolbarLogoUrl();
      this.toolbarLogoError.set(false);
    },
    { allowSignalWrites: true }
  );
  private readonly loadNotificationSettings = effect(() => {
    const user = this.currentUser();
    const organisationId = this.userContext.currentOrganisationId();
    const applicationDataLoaded = this.dataService.loaded();
    if (applicationDataLoaded && (user?.role === 'admin' || user?.role === 'office') && organisationId && !this.isRootMode()) {
      void this.dataService.getOrganisationSettings(organisationId).catch(() => undefined);
    }
  });
  toggleMobileMenu(): void {
    if (this.isContextMode()) {
      return;
    }
    this.mobileMenuOpen.set(!this.mobileMenuOpen());
  }

  closeMobileMenu(): void {
    if (this.isMobile()) {
      this.mobileMenuOpen.set(false);
    }
  }

  openCurrentInstructorCalendar(): void {
    const instructorId = this.userContext.currentInstructorId();
    if (instructorId == null) return;
    const instructor = this.dataService.instructors().find((item) => this.sameId(item.id, instructorId));
    const instructorName = instructor
      ? `${instructor.name} ${instructor.surname}`.trim()
      : this.userContext.currentUser().displayName;
    void import('./pages/resource-details/instructor-calendar.component').then(({ InstructorCalendarDialogComponent }) => {
      this.dialog.open(InstructorCalendarDialogComponent, {
        width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', panelClass: 'fullscreen-calendar-dialog',
        data: { instructorId, instructorName, instructorPortalMode: true }
      });
    });
  }

  switchUser(userId: number): void {
    this.userContext.setCurrentUser(userId);
  }

  switchLanguage(language: LanguageCode): void {
    this.userContext.setCurrentLanguage(language);
  }

  languageCode(language: LanguageCode): string {
    return language.toUpperCase();
  }

  onToolbarLogoError(): void {
    this.toolbarLogoError.set(true);
  }

  async logoutToContext(): Promise<void> {
    this.mobileMenuOpen.set(false);
    await this.auth.logout();
    this.userContext.clearAuthenticatedUser();
    this.dataService.clearSessionData();
    await this.router.navigate(['/login']);
  }

  async endRootSupportSession(): Promise<void> {
    this.mobileMenuOpen.set(false);
    const rootSession = await this.auth.endDelegatedOrganisationSession();
    if (rootSession?.user) {
      this.userContext.setAuthenticatedUser(rootSession.user);
    } else {
      this.userContext.setRootContext();
    }
    await this.dataService.reload();
    await this.router.navigate(['/admin']);
  }

  openChangePasswordDialog(): void {
    const dialogRef = this.dialog.open(ChangePasswordDialogComponent, {
      width: '480px',
      maxWidth: 'calc(100vw - 32px)'
    });

    dialogRef.afterClosed().subscribe((changed) => {
      if (changed) {
        this.snackBar.open(this.localization.translate('account.changePassword.success'), 'OK', {
          duration: 4000,
          panelClass: ['app-snackbar-success']
        });
      }
    });
  }

  private isAdminConsoleUrl(url: string): boolean {
    return url === '/admin' || (url.startsWith('/admin/') && !url.startsWith('/admin/users'));
  }

  private isPublicAuthUrl(url: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    return path === '/login' || path === '/activate-account' || path === '/reset-password';
  }
}
